import React, {useEffect} from 'react';

import Button, {ButtonSizes, ButtonTypes} from '../../components/ButtonNew';
import DocumentHeader from '../../components/DocumentHeader';
import FAQs from '../../components/FAQs';
import {useAnalytics, useAppContext} from '../../hooks';
import {
  faqItems,
  heroStats,
  howItWorks,
  vettingPillars,
  workTypes,
} from './data';
import {getBrandName} from '../../components/Brand';

const SectionHeading = ({eyebrow, title, subtitle}) => (
  <div className="max-w-2xl">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600">{eyebrow}</p>
    <h2 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl" style={{fontFamily: 'Sora, sans-serif'}}>
      {title}
    </h2>
    {subtitle && <p className="mt-4 text-base leading-relaxed text-gray-600">{subtitle}</p>}
  </div>
);

const TalentNetworkPage = () => {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();

  useEffect(() => {
    tracker.jobsView({page: 'jobs-ai'});
  }, []);

  // The application is the Job matching card on the user's profile — one
  // intake path shared with the dashboard nudge, no separate form to keep
  // in sync.
  const applyHref = currentUser
    ? `/users/${currentUser.uuid}?src=jobs-ai#job-matching`
    : '/signup?src=jobs-ai&returnPath=/jobs/ai';
  const onClickApply = () => {
    tracker.jobsClickApply({page: 'jobs-ai', authenticated: Boolean(currentUser)});
  };

  return (
    <div className="min-h-screen bg-white">
      <DocumentHeader
        title={`${getBrandName()} Talent Network: Get Paid to Train Frontier AI`}
        metaDescription={`Join the ${getBrandName()} Talent Network — vetted engineers and domain experts building benchmarks, evals, and training data for frontier AI labs. Remote, flexible, $50–150+/hr.`}
      />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-slate-950">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,black,transparent)]"
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 left-1/2 h-96 w-[48rem] -translate-x-1/2 rounded-full bg-indigo-600/25 blur-3xl"
        />
        <div className="relative mx-auto max-w-5xl px-4 pb-16 pt-14 sm:px-6 sm:pb-20 sm:pt-20">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-300">
              {`${getBrandName()} Talent Network`}
            </p>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              Now accepting applications
            </span>
          </div>
          <h1
            className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl md:text-6xl"
            style={{fontFamily: 'Sora, sans-serif'}}
          >
            Get paid to teach AI what you know.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {`Frontier AI labs pay ${getBrandName()}-vetted engineers and domain experts to build the
            benchmarks, evals, and training data behind the next generation of models.
            Remote, flexible, and paid at expert rates — part-time or full-time.`}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button size={ButtonSizes.LARGE} href="#apply">
              Apply to join
            </Button>
            <Button size={ButtonSizes.LARGE} buttonType={ButtonTypes.SECONDARY} href="#how-it-works">
              How it works
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {heroStats.map((stat) => (
              <div key={stat.label} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm">
                <div className="text-xl font-semibold tabular-nums tracking-tight text-white sm:text-2xl" style={{fontFamily: 'Sora, sans-serif'}}>
                  {stat.value}
                </div>
                <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The work ─────────────────────────────────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
          <SectionHeading
            eyebrow="The work"
            title="Knowledge work, not labeling."
            subtitle="Labs don't need clicks — they need judgment. Four kinds of projects flow through the network, all remote and scoped with rates posted up front."
          />
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {workTypes.map((work) => (
              <div
                key={work.title}
                className="group rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-[0_16px_40px_-20px_rgba(30,27,75,0.3)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-semibold tracking-tight text-gray-900" style={{fontFamily: 'Sora, sans-serif'}}>
                    {work.title}
                  </h3>
                  <span className="shrink-0 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold tabular-nums text-indigo-700">
                    {work.rate}
                  </span>
                </div>
                <p className="mt-2.5 text-sm leading-relaxed text-gray-600">{work.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {work.domains.map((domain) => (
                    <span key={domain} className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-medium text-gray-500">
                      {domain}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────── */}
      <section id="how-it-works" className="scroll-mt-14 border-y border-gray-100 bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
          <SectionHeading
            eyebrow="How it works"
            title="Apply once. Get matched as demand comes in."
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {howItWorks.map((item) => (
              <div key={item.step} className="relative rounded-2xl border border-gray-200 bg-white p-6">
                <span className="text-3xl font-semibold tabular-nums text-indigo-200" style={{fontFamily: 'Sora, sans-serif'}}>
                  {item.step}
                </span>
                <h3 className="mt-3 text-base font-semibold tracking-tight text-gray-900">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Vetting ──────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-slate-950">
        <div
          aria-hidden="true"
          className="absolute -bottom-40 left-1/2 h-96 w-[52rem] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-300">Quality</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold tracking-tight text-white sm:text-4xl" style={{fontFamily: 'Sora, sans-serif'}}>
            A talent pool that stays sharp.
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {vettingPillars.map((pillar) => (
              <div key={pillar.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
                <h3 className="text-base font-semibold tracking-tight text-white">{pillar.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Apply ────────────────────────────────────────────── */}
      <section id="apply" className="scroll-mt-14 bg-white">
        <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600">Apply</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl" style={{fontFamily: 'Sora, sans-serif'}}>
              Join the network.
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-gray-600">
              Five minutes to apply, then you&apos;re on the waitlist. Your name stays private —
              partners see an anonymized profile until you accept an introduction.
            </p>
          </div>
          <div className="mt-10 rounded-2xl border border-gray-200 bg-slate-50 p-6 text-center sm:p-8">
            {currentUser ? (
              <>
                <p className="text-sm leading-relaxed text-gray-600">
                  {`Your application is the job matching profile on your ${getBrandName()}
                  account — your practice history and peer reviews already count
                  toward it.`}
                </p>
                <div className="mt-5">
                  <Button size={ButtonSizes.LARGE} href={applyHref} onClick={onClickApply}>
                    Complete your job matching profile
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm leading-relaxed text-gray-600">
                  {`Applying takes a free ${getBrandName()} account — your practice history
                  and peer reviews strengthen your application from day one.`}
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
                  <Button size={ButtonSizes.LARGE} href={applyHref} onClick={onClickApply}>
                    Sign up to apply
                  </Button>
                  <Button
                    size={ButtonSizes.LARGE}
                    buttonType={ButtonTypes.SECONDARY}
                    href="/login?src=jobs-ai&returnPath=/jobs/ai"
                  >
                    Log in
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section className="border-t border-gray-100 bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
          <SectionHeading eyebrow="FAQ" title="Questions, answered." />
          <div className="mt-8">
            <FAQs items={faqItems} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TalentNetworkPage;
